<template>
    <div class="gas-species-block">
        <div class="gas-species-block-header">
            <div class="gas-species-block-title-wrap">
                <span class="gas-species-block-title">{{ title }}</span>
                <span class="gas-species-block-count">{{ localRows.length }}개 종</span>
                <span class="gas-species-optional">선택 입력</span>
            </div>
            <p class="gas-species-block-hint">{{ hint }}</p>
        </div>
        <div class="gas-species-rows">
            <div v-for="(row, idx) in localRows" :key="'sp-' + basket + '-' + idx" class="gas-species-row">
                <span class="gas-species-row-index">{{ idx + 1 }}</span>
                <div class="gas-species-row-code">
                    <label class="gas-species-field-label">종 코드</label>
                    <input
                        v-model.trim="row.componentCode"
                        type="text"
                        class="form-control form-control-sm"
                        :placeholder="placeholderCode"
                        @input="emitRows"
                        @blur="emitRows"
                    />
                </div>
                <div class="gas-species-row-value">
                    <label class="gas-species-field-label">배출계수</label>
                    <div class="input-group input-group-sm">
                        <input
                            v-model.number="row.emissionFactor"
                            type="number"
                            step="0.0001"
                            min="0"
                            class="form-control"
                            placeholder="0"
                            @input="emitRows"
                            @blur="emitRows"
                        />
                        <div class="input-group-append">
                            <span class="input-group-text gas-species-unit-suffix">{{ gasUnit }}</span>
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    class="gas-species-row-remove"
                    :disabled="localRows.length <= 1"
                    title="행 삭제"
                    @click="removeRow(idx)"
                >
                    <i class="fas fa-trash-alt"></i>
                </button>
            </div>
        </div>
        <button type="button" class="gas-species-add-row" @click="addRow">종 행 추가</button>
    </div>
</template>

<script>
export default {
    name: "FactorGasSpeciesBlock",
    props: {
        basket: {
            type: String,
            required: true,
            validator: (v) => v === "HFCs" || v === "PFCs",
        },
        rows: {
            type: Array,
            default: () => [],
        },
        gasUnit: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            localRows: [],
        };
    },
    computed: {
        title() {
            return this.basket === "HFCs" ? "수소불화탄소류 (HFCs)" : "과불화탄소류 (PFCs)";
        },
        hint() {
            return this.basket === "HFCs"
                ? "집계 단위는 HFCs이며 필요 시에만 입력합니다. 종마다 코드(예: HFC-134a, HFC-125)와 계수를 구분해 등록합니다."
                : "집계 단위는 PFCs이며 필요 시에만 입력합니다. 종마다 코드(예: PFC-14, CF4)와 계수를 구분해 등록합니다.";
        },
        placeholderCode() {
            return this.basket === "HFCs" ? "예: HFC-134a" : "예: PFC-14";
        },
    },
    watch: {
        rows: {
            handler(next) {
                this.syncFromProp(next);
            },
            immediate: true,
        },
    },
    methods: {
        syncFromProp(next) {
            const src =
                Array.isArray(next) && next.length > 0
                    ? next.map((r) => ({ ...r }))
                    : [{ componentCode: "", emissionFactor: null }];
            this.localRows = src;
        },
        emitRows() {
            this.$emit(
                "update:rows",
                this.localRows.map((r) => ({ ...r })),
            );
        },
        addRow() {
            this.localRows.push({ componentCode: "", emissionFactor: null });
            this.emitRows();
        },
        removeRow(idx) {
            if (this.localRows.length <= 1) return;
            this.localRows.splice(idx, 1);
            this.emitRows();
        },
    },
};
</script>

<style scoped>
.gas-species-block {
    margin-top: 8px;
    padding: 8px;
    border: 0;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.76);
    max-width: 760px;
    margin-left: auto;
    margin-right: auto;
}

.gas-species-block + .gas-species-block {
    margin-top: 6px;
}

.gas-species-block-header {
    margin-bottom: 8px;
}

.gas-species-block-title-wrap {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;
}

.gas-species-block-title {
    font-size: 11px;
    font-weight: 700;
    color: #32485d;
}

.gas-species-block-count {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: 999px;
    border: 0;
    background: #edf4fa;
    font-size: 9px;
    font-weight: 700;
    color: #60778d;
}

.gas-species-optional {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: 999px;
    border: 0;
    background: #f1f5f9;
    font-size: 9px;
    font-weight: 700;
    color: #708295;
}

.gas-species-block-hint {
    margin: 3px 0 0;
    font-size: 10px;
    line-height: 1.35;
    color: #6f8398;
}

.gas-species-rows {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.gas-species-row {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) minmax(0, 1.15fr) auto;
    align-items: flex-end;
    gap: 6px;
    padding: 6px;
    border: 0;
    border-radius: 8px;
    background: rgba(246, 250, 253, 0.96);
}

.gas-species-row-index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 30px;
    border-radius: 7px;
    border: 0;
    background: rgba(255, 255, 255, 0.94);
    color: #6f8398;
    font-size: 9px;
    font-weight: 700;
}

.gas-species-row-code {
    min-width: 0;
}

.gas-species-row-value {
    min-width: 0;
}

.gas-species-field-label {
    display: block;
    margin-bottom: 3px;
    font-size: 9px;
    font-weight: 600;
    color: #5a6f83;
}

.gas-species-row-code .form-control,
.gas-species-row-value .form-control {
    height: 30px;
    border: 1px solid #d8e2ec;
    border-radius: 8px;
    font-size: 11px;
    box-shadow: none;
    background: rgba(255, 255, 255, 0.95);
}

.gas-species-row-code .form-control:focus,
.gas-species-row-value .form-control:focus {
    border-color: #4d84ac;
    box-shadow: 0 0 0 2px rgba(77, 132, 172, 0.14);
}

.gas-species-unit-suffix {
    font-size: 9px;
    color: #54697f;
    font-weight: 600;
    border: none;
    border-left: 0;
    background: transparent;
}

.gas-species-row-remove {
    flex: 0 0 auto;
    padding: 0 9px;
    height: 30px;
    font-size: 10px;
    font-weight: 600;
    border: 0;
    background: rgba(255, 255, 255, 0.94);
    color: #60778d;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
}

.gas-species-row-remove:hover:not(:disabled) {
    background: #edf4fa;
}

.gas-species-row-remove:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.gas-species-add-row {
    margin-top: 6px;
    padding: 4px 10px;
    font-size: 10px;
    font-weight: 600;
    border: 0;
    background: #eef4fa;
    color: #5c7388;
    border-radius: 8px;
    cursor: pointer;
}

.gas-species-add-row:hover {
    background: #e4edf6;
}

@media (max-width: 767px) {
    .gas-species-block {
        max-width: 100%;
    }

    .gas-species-row {
        grid-template-columns: 1fr;
        gap: 6px;
    }

    .gas-species-row-index {
        width: 34px;
        height: 24px;
    }

    .gas-species-row-remove {
        width: 100%;
    }
}
</style>
